import { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Info, ChevronDown } from 'lucide-react';

interface DivisionSelectorProps {
  selectedDivision: string;
  onDivisionChange: (division: string) => void;
}

const DIVISIONS = [
  { value: 'mens', label: "Men's Division" },
  { value: 'womens', label: "Women's Division" },
  { value: 'coed', label: 'Co-ed Division' },
  { value: 'open', label: 'Open Division (All Participants Welcome)' },
];

export const DivisionSelector = ({ selectedDivision, onDivisionChange }: DivisionSelectorProps) => {
  const [showOpenInfo, setShowOpenInfo] = useState(false);

  const handleChange = (value: string) => {
    if (value === 'open') {
      setShowOpenInfo(true);
    }
    onDivisionChange(value);
  };

  return (
    <>
      <div>
        <Label htmlFor="division_select">Division / Gender Category *</Label>
        <Select value={selectedDivision} onValueChange={handleChange}>
          <SelectTrigger>
            <SelectValue placeholder="Select division" />
          </SelectTrigger>
          <SelectContent className="bg-background border z-[9999] max-h-60 overflow-y-auto">
            {DIVISIONS.map((div) => (
              <SelectItem key={div.value} value={div.value}>
                {div.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Open Division Information Dialog */}
      <Dialog open={showOpenInfo} onOpenChange={setShowOpenInfo}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-primary" />
              Open Division — All Participants Welcome
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 text-sm text-muted-foreground">
            <p>This division is open to all participants.</p>
            <p>
              Team participation, eligibility, and roster decisions are determined by individual teams,
              leagues, or event organizers—not Players Lockeroom.
            </p>
            <p>
              Our platform is designed to provide visibility, connection, and opportunity within sports
              communities.
            </p>

            <Collapsible>
              <CollapsibleTrigger className="flex items-center gap-2 text-primary font-medium hover:underline">
                <ChevronDown className="h-4 w-4" />
                More info
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-3 space-y-3 pl-6 border-l-2 border-muted">
                <p>
                  Players Lockeroom does not create, control, or enforce team or league participation rules.
                </p>
                <p>
                  Each organization may establish its own guidelines based on its preferences, structure,
                  or governing standards.
                </p>
              </CollapsibleContent>
            </Collapsible>
          </div>

          <DialogFooter>
            <Button onClick={() => setShowOpenInfo(false)}>I Understand</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
